import { deleteRequest, postRequest, putRequest } from "lib/core/apiClient";
import { getAccessToken, refreshExpiredIdToken } from "lib/utils/auth";
import { parseAuthenticateMeAccount } from "lib/utils/authenticateMe";

export type CustomerEmail = {
  email: string;
  primary?: boolean;
  verified?: boolean;
};

const normalizeEmailKey = (value: string) => value.trim().toLowerCase();

const ensureValidAuthToken = async () => {
  try {
    await refreshExpiredIdToken();
    getAccessToken();
  } catch (error) {
    console.error("Failed to refresh auth token before customer email API call:", error);
  }
};

export const getLoginEmailsFromAccount = (raw: unknown): CustomerEmail[] => {
  const payload = parseAuthenticateMeAccount(raw);

  const primaryNormalized = normalizeEmailKey(
    (payload.customer_config?.email ?? payload.email ?? "").toString(),
  );

  const byEmail = new Map<string, { email: string; verified: boolean }>();

  for (const identifier of payload.auth_identifiers ?? []) {
    if (identifier.oid_type !== "EMAIL" || !identifier.oid?.trim()) {
      continue;
    }
    const displayEmail = identifier.oid.trim();
    const key = normalizeEmailKey(displayEmail);
    const existing = byEmail.get(key);
    const verified = Boolean(identifier.verified) || Boolean(existing?.verified);
    if (!existing) {
      byEmail.set(key, { email: displayEmail, verified });
    } else {
      byEmail.set(key, { email: existing.email, verified });
    }
  }

  const rows = Array.from(byEmail.values()).map((row) => ({
    email: row.email,
    verified: row.verified,
    primary: Boolean(primaryNormalized) && normalizeEmailKey(row.email) === primaryNormalized,
  }));

  rows.sort((a, b) => {
    if (a.primary !== b.primary) {
      return a.primary ? -1 : 1;
    }
    if (a.verified !== b.verified) {
      return a.verified ? -1 : 1;
    }
    return a.email.localeCompare(b.email);
  });

  return rows;
};

export const addCustomerEmail = async (email: string) => {
  await ensureValidAuthToken();

  return postRequest(
    "/customer-account/emails",
    {
      email,
    },
    "AUTH_PRIVATE",
  );
};

export const editCustomerEmail = async (oldEmail: string, newEmail: string) => {
  await ensureValidAuthToken();

  return putRequest(
    "/customer-account/emails",
    {
      old_email: oldEmail,
      new_email: newEmail,
    },
    "AUTH_PRIVATE",
  );
};

export const deleteCustomerEmail = async (email: string) => {
  await ensureValidAuthToken();

  return deleteRequest(
    "/customer-account/emails",
    {
      email,
    },
    "AUTH_PRIVATE",
  );
};

export const setPrimaryCustomerEmail = async (email: string) => {
  await ensureValidAuthToken();

  return putRequest(
    "/customer-account/emails/set-primary",
    {
      email,
    },
    "AUTH_PRIVATE",
  );
};

export const updateCustomerName = async ({ firstName, lastName }: { firstName: string; lastName: string }) => {
  await ensureValidAuthToken();

  return putRequest(
    "/customer-account/name",
    {
      first_name: firstName,
      last_name: lastName,
    },
    "AUTH_PRIVATE",
  );
};
